import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react'

import {COUNTRIES, getCountry} from './CountrySelector.utils'
import {useCountryRegistry} from '../../../utils/country-registry'

import {
  SelectorWrapper,
  SearchInput,
  ChevronSmall,
  DropdownPanel,
  CountryList,
  CountryOption,
  NoResults,
} from './CountrySelector.styles'

// ─────────────────────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────────────────────

function ChevronDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path
        d="M2 3.5l3 3 3-3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export function CountrySelector({
  value,
  onChange,
  disabled = false,
  countries: countriesProp,
  outlined = false,
}) {
  useCountryRegistry() // re-render when the backend country list loads
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const wrapperRef = useRef(null)

  const countryList = countriesProp ?? COUNTRIES

  const selected = getCountry(value)

  // Keep the input text showing the selected country's name whenever
  // it isn't actively being searched.
  useEffect(() => {
    if (!isOpen) setQuery(selected?.label ?? '')
  }, [selected, isOpen])

  // Nothing is shown until the user starts typing — the full list isn't
  // dumped on open; matches narrow in as characters are entered.
  const filteredCountries = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    return countryList.filter(country =>
      country.label.toLowerCase().includes(q),
    )
  }, [countryList, query])

  const handleSelect = useCallback(
    code => {
      onChange?.(code)
      setIsOpen(false)
      setHighlightedIndex(-1)
    },
    [onChange],
  )

  // Clear the search text on open so typing starts from a blank slate
  // instead of the field being pre-filled with the current selection.
  const openList = useCallback(() => {
    if (disabled) return
    setIsOpen(true)
    setQuery('')
    setHighlightedIndex(-1)
  }, [disabled])

  // Close on outside click
  useEffect(() => {
    const handleOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutside)

    return () => {
      document.removeEventListener('mousedown', handleOutside)
    }
  }, [])

  const handleKeyDown = e => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        openList()
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex(prev =>
        Math.min(prev + 1, filteredCountries.length - 1),
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const option = filteredCountries[highlightedIndex]
      if (option) handleSelect(option.code)
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setQuery(selected?.label ?? '')
    }
  }

  return (
    <SelectorWrapper ref={wrapperRef}>
      <SearchInput
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        // Chrome ignores `off` for saved-address fields. This value prevents
        // its profile-autofill popup while leaving the custom country search intact.
        autoComplete="new-password"
        disabled={disabled}
        outlined={outlined}
        value={query}
        placeholder="Select Country"
        onFocus={openList}
        onClick={openList}
        onChange={e => {
          setIsOpen(true)
          setQuery(e.target.value)
          setHighlightedIndex(-1)
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => setQuery(selected?.label ?? '')}
      />

      <ChevronSmall isOpen={isOpen}>
        <ChevronDown />
      </ChevronSmall>

      {isOpen && (
        <DropdownPanel>
          <CountryList role="listbox">
            {filteredCountries.length === 0 && (
              <NoResults>
                {query.trim() ? 'No countries found' : 'Start typing to search'}
              </NoResults>
            )}

            {filteredCountries.map((country, index) => {
              const isSelected = country.code === value
              const isHighlighted = index === highlightedIndex

              return (
                <CountryOption
                  key={country.code}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  isSelected={isSelected}
                  isHighlighted={isHighlighted}
                  onMouseDown={e => e.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleSelect(country.code)}
                >
                  {country.label}
                </CountryOption>
              )
            })}
          </CountryList>
        </DropdownPanel>
      )}
    </SelectorWrapper>
  )
}
